import { useMemo } from "react";

import { UIView, useCurrentStateAndParams } from "@uirouter/react";
import { ErrorBoundary } from "react-error-boundary";
import styled from "styled-components";

import { useAllKeywordGroups } from "app/services/keyword-groups/hooks";
import { FloatingFrameProvider } from "app/components/ai-kit/FloatingFrameProvider";
import { FloatingFrameWrapper } from "app/components/ai-kit/FloatingFrameWrapper";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import useStickySubNav from "app/components/sub-navigation-bar/UseStickySubNav";
import { KeywordListsContextProvider } from "app/modules/demand-analysis/keyword-lists";

import { KeywordListsComparisonNav } from "./features/keyword-lists-comparison-nav";
import type { DemandAnalysisSearchTrendsQueryParams } from "./types";

const Section = styled.section`
    height: 100%;
    display: flex;
`;

const FlexContainer = styled.span`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-x: hidden;
    width: 100%;
`;

export const DemandAnalysisSearchTrendsRoot = () => {
    const { SubNavWrapper, PageContentWrapper, subNavWrapperProps, pageContentWrapperProps } = useStickySubNav();
    const { params, state } = useCurrentStateAndParams<DemandAnalysisSearchTrendsQueryParams>();
    const { userGroups, sharedGroups } = useAllKeywordGroups();

    const combinedKeywordGroupsData = useMemo(() => {
        const userKeywordGroups = userGroups ?? [];
        const sharedKeywordGroups = sharedGroups ?? [];
        return userKeywordGroups.concat(sharedKeywordGroups);
    }, [userGroups, sharedGroups]);

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            resetKeys={[state, params]}
        >
            <KeywordListsContextProvider
                ids={params.id}
                keywordGroupsData={combinedKeywordGroupsData}
            >
                <Section className="keyword-analysis-search sw-layout-no-scroll-container">
                    <FlexContainer className="u-flex-row u-flex-column">
                        <SubNavWrapper
                            className="sw-layout-subnav"
                            {...subNavWrapperProps}
                        >
                            <KeywordListsComparisonNav preventShowEducationBar />
                        </SubNavWrapper>
                        <FloatingFrameProvider>
                            <PageContentWrapper
                                className="sw-layout-research sw-layout-scrollable-element use-sticky-css-rendering"
                                {...pageContentWrapperProps}
                            >
                                <div
                                    data-automation="SearchTrendsContainer"
                                    className="sw-layout-page sw-page-websiteAnalysis"
                                >
                                    <ErrorBoundary
                                        FallbackComponent={ErrorFallback}
                                        resetKeys={[state, params]}
                                    >
                                        <FloatingFrameWrapper />
                                        <UIView />
                                    </ErrorBoundary>
                                </div>
                            </PageContentWrapper>
                        </FloatingFrameProvider>
                    </FlexContainer>
                </Section>
            </KeywordListsContextProvider>
        </ErrorBoundary>
    );
};
